import React, { useMemo } from 'react';
import moment from 'moment/moment';
import cn from '@/utils/cn';
import I18n from '@/modules/i18n';
import Icon, { ValidIcon } from '@/components/ui/Icon';
import Tooltip from '@/components/ui/Tooltip';
import assignmentLmsKind from '@/components/shared/Lms/assignmentLmsKind';
import { lmsProviderConfig } from '@/components/shared/Lms/lmsProviders';
import { ISubmission, IAssignment } from '@/components/interfaces';
import { getSubmissionScore } from '@/components/staffer/AssignmentsBasedGradesTable/Table/utils';
import { IStudent, StatusType } from '../../types';
import useGradesTableStore from '../../store/useGradesTableStore';

interface StatusIndicatorIconProps {
  className?: string;
  type: StatusType;
}

export const STATUS_INFO: Record<StatusType, {
  color: string;
  label: string;
}> = {
  inProgress: { color: '#ffca17', label: I18n.t('in_progress') },
  late: { color: '#D14801', label: I18n.t('late') },
  lowScore: { color: '#d32c23', label: I18n.t('low_score') },
  notAssigned: { color: '#555555', label: I18n.t('not_assigned') },
  notStarted: { color: 'transparent', label: I18n.t('not_started') },
  submitted: { color: '#78962d', label: I18n.t('turned_in') },
};

interface GetStatusProps {
  assignment: {
    due_datetime?: string;
    max_points: number | string;
  };
  isAssigned: boolean;
  lowScoresSettings?: {
    enabled: boolean;
    threshold: number;
  };
  submission: Partial<ISubmission>;
}

export const getStatus = ({ assignment, isAssigned, lowScoresSettings, submission }: GetStatusProps) => {
  const grade = getSubmissionScore(submission as ISubmission, '-');
  const hasGrade = typeof grade === 'number';

  const isLowScore = () => {
    if (!(lowScoresSettings || {}).enabled) return false;
    if (!hasGrade) return false;

    return grade < parseFloat(assignment.max_points.toString()) * (lowScoresSettings.threshold / 100.0);
  };

  const isLate = () => {
    if (!submission.first_submitted_at || !assignment.due_datetime) return false;

    return new Date(submission.first_submitted_at) > new Date(assignment.due_datetime);
  };

  return useMemo<StatusType>(() => {
    if (!isAssigned) return 'notAssigned';
    if (!submission) return 'notStarted';
    if (submission.submitted_at && isLate()) return 'late';
    if (submission.submitted_at && isLowScore()) return 'lowScore';
    if (submission.submitted_at) return 'submitted';
    if (!submission.in_progress) return 'notStarted';

    return 'inProgress';
  }, [isAssigned, submission]);
};

export const getStatusLabel = ({ completedText, status }: { completedText?: string, status: StatusType }) => {
  if (status === 'submitted' && completedText) return completedText;

  return STATUS_INFO[status]?.label;
};

const CIRCLE_SIZE = 10;
const SKIP_SIZE = 14;

const NON_CIRCLE_ICONS: Partial<Record<StatusType, ValidIcon>> = { late: 'Time', notAssigned: 'Skip' };

export const StatusIndicatorIcon = ({ className, type }: StatusIndicatorIconProps) => {
  if (type === 'notStarted') return null;

  const icon = NON_CIRCLE_ICONS[type] ?? 'Circle';
  const size = icon === 'Circle' ? CIRCLE_SIZE : SKIP_SIZE;
  const { color, label } = STATUS_INFO[type];

  return (
    <span
      aria-label={label}
      className={cn('tw-flex tw-items-center tw-justify-center', className)}
      role="img"
      style={{
        height: size,
        width: size,
      }}
    >
      <Icon
        fill={color}
        height={size}
        icon={icon}
        width={size}
      />
    </span>
  );
};

interface StatusIndicatorProps {
  assignment: IAssignment;
  isAssigned?: boolean;
  student: IStudent;
  submission: ISubmission;
}

const StatusIndicator = ({ assignment, isAssigned, student, submission }: StatusIndicatorProps) => {
  const lowScoresSettings = useGradesTableStore(state => state.lowScoresSettings);
  const showPercentages = useGradesTableStore(state => state.showPercentages);
  const lastSyncEvent = useGradesTableStore(
    state => state.gradeSyncEvents[assignment.id]?.[student.id],
  );
  const status = getStatus({ assignment, isAssigned, lowScoresSettings, submission });

  const lmsKind = assignmentLmsKind(assignment);
  const lmsProvider = lmsKind ? lmsProviderConfig(lmsKind).name : '';

  const isEmptyType = ['notStarted', 'notAssigned'].includes(status);

  const formatTime = (timeStamp: string | undefined) => {
    if (!timeStamp) return 'N/A';

    const localTime = moment(timeStamp).local();
    const timezone = moment.tz(moment.tz.guess()).zoneAbbr();

    if (I18n.locale === 'es') return `${localTime.format('D [de] MMM, h:mmA')} (${timezone})`;

    return `${localTime.format('MMM DD, hh:mmA')} (${timezone})`;
  };

  return (
    <Tooltip.Provider delayDuration={0}>
      <Tooltip>
        <Tooltip.Trigger asChild>
          <div
            className={cn('tw-absolute', {
              'tw-mr-12': !isEmptyType && !showPercentages,
              'tw-mr-14': !isEmptyType && showPercentages,
            })}
          >
            <StatusIndicatorIcon type={status} />
          </div>
        </Tooltip.Trigger>
        <Tooltip.Content
          className="tw-bg-white tw-shadow-md tw-rounded tw-py-2 tw-px-4 tw-text-center tw-z-9999"
          disabled={isEmptyType}
          side="right"
          sideOffset={2}
        >
          <div className="tw-flex tw-flex-col tw-text-sm">
            <div className="tw-flex tw-items-center">
              <span className="tw-mr-2">{I18n.t('status')}:</span>
              <StatusIndicatorIcon className="tw-mr-1" type={status} />
              <span>{getStatusLabel({ status })}</span>
            </div>

            <div className="tw-flex tw-items-center">
              <span className="tw-mr-2">{I18n.t('turned_in')}:</span>
              <span className={cn({ 'tw-text-med-grey': !submission?.submitted_at })}>
                {formatTime(submission?.submitted_at)}
              </span>
            </div>

            {lmsProvider && student.has_lms_account && (
              <div className="tw-flex tw-items-center">
                <span className="tw-mr-2">{I18n.t('last_synced_on')} {lmsProvider}:</span>
                {/* PLAT-18052: the last *successful* sync. It previously showed the last attempt,
                    so a failed retry re-dated a row whose grade never reached the LMS again. */}
                <span className={cn({ 'tw-text-med-grey': !lastSyncEvent?.last_success_at })}>
                  {formatTime(lastSyncEvent?.last_success_at)}
                </span>
              </div>
            )}
          </div>

          <Tooltip.Arrow className="tw-fill-white tw-bg-transparent" />
        </Tooltip.Content>
      </Tooltip>
    </Tooltip.Provider>
  );
};

export default StatusIndicator;
